import styled from 'styled-components'
import { Text } from '../../../../../components/Ui-Kits/Text'
import BaseButton from '../../../../../components/Button/Button'
import { Col, Row } from '../../../../../components/Ui-Kits/Grid'
import { Select } from 'components/Ui-Kits/Select'
import { useCountries } from 'hooks/useCountries'
import { useAppTranslation } from 'i18n/hooks'
import { useMemo, useState } from 'react'
import { Loading, LoadingWrap } from 'components/Shared/Loading'
import { useShippingMethods } from './useShippingMethods'
import { string } from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { theme } from 'globalStyles'
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons'
import { useProfile } from 'hooks/useProfile'
import { contactUrls } from 'constants/index'
import qs from 'querystring'

export const ShippingBox = ({ containerSize, sku }) => {
  const [countries, loading] = useCountries()
  const {
    t,
    i18n: { language },
  } = useAppTranslation()
  const [country, setCountry] = useState('FI')
  const [methods, methodsLoading, updateCountry] = useShippingMethods(containerSize || 'small-item')
  const [open, setOpen] = useState(false)
  const { profile } = useProfile()

  const isSpecial = containerSize === 'special-handling'
  const isOversize = containerSize === 'oversize'

  const askMoreURL = useMemo(() => {
    const url = contactUrls[language]
    const query = {
      sku: sku,
    }
    if (profile) {
      Object.assign(query, {
        firstname: profile.firstName,
        lastname: profile.lastName,
        email: profile.email,
        phone: profile.phone,
      })
    }

    return `${url}?${qs.stringify(query)}#contact`
  }, [profile, language, sku])

  const askMoreLink = (
    <A color="black" href={askMoreURL} bold>
      {t('ascAboutShippingCost')}
    </A>
  )

  const getItemPrice = method => {
    if (method.method === 'Local pickup') return method.price + ' €'

    if (containerSize === 'cannot-be-delivered') return t('canNotBeDelivered')

    return method.price ? method.price + ' €' : askMoreLink
  }

  if (isSpecial || isOversize)
    return (
      <Wrap>
        <TextWrap onClick={() => setOpen(open => !open)}>
          <Text color="primary" bold size={1}>
            {t('shipping')}
          </Text>
          <Icon icon={open ? faChevronUp : faChevronDown} />
        </TextWrap>
        <Content open={open}>
          <BaseButton
            color="primary"
            secondary
            fullWidth
            onClick={() => {
              window.location.href = askMoreURL
            }}
          >
            {t('ascAboutShippingCost')}
          </BaseButton>
        </Content>
      </Wrap>
    )

  return (
    <Wrap>
      <TextWrap onClick={() => setOpen(open => !open)}>
        <Text color="primary" bold size={1}>
          {t('shipping')}
        </Text>
        <Icon icon={open ? faChevronUp : faChevronDown} />
      </TextWrap>
      <Content open={open}>
        <Row mb={1}>
          <Col col={12} pr={0.75}>
            <Select
              noBorder
              label={t('country')}
              valueKey="code"
              value={country}
              options={countries}
              onChange={value => setCountry(value)}
            />
          </Col>
        </Row>
        <Button color="primary" secondary onClick={() => updateCountry(country)}>
          {t('getPrice')}
        </Button>
        {methods.map(method => (
          <TextWrap align="center" key={method.method}>
            <FlexWrap>
              <Text color="primary" bold aria-setsize={1.5}>
                {method.method === 'Local pickup' ? t('localPickup') : method.method}
              </Text>
            </FlexWrap>
            {getItemPrice(method)}
          </TextWrap>
        ))}
        {(loading || methodsLoading) && (
          <LoadingWrap>
            <Loading />
          </LoadingWrap>
        )}
      </Content>
    </Wrap>
  )
}

ShippingBox.propTypes = {
  containerSize: string,
  sku: string,
}

const Wrap = styled.div`
  padding: 1rem;
  background-color: var(--color-grey);
  margin-bottom: 1.5rem;
  position: relative;
`
const TextWrap = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: ${({ justify = 'space-between' }) => justify};
  align-items: ${({ align = 'flex-start' }) => align};
  margin-bottom: 1rem;

  &:first-child {
    margin-bottom: 0;
  }
`
const FlexWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const A = styled.a`
  color: black;
  &:hover {
    text-decoration: underline;
  }
`

const Button = styled(BaseButton)`
  width: 100%;
  display: block;
  margin-bottom: 1rem;
`
const Icon = styled(FontAwesomeIcon)`
  font-size: 1rem;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  color: var(--color-primary);
  display: inline-block;
  cursor: pointer;
  display: block;
  ${theme.breakpoints.tablet} {
    display: none;
  }
`
const Content = styled.div`
  display: ${({ open }) => (open ? 'block' : 'none')};
  margin-top: 1rem;
  ${theme.breakpoints.tablet} {
    display: block;
  }
`
